import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Box,
  Button,
  Stack,
  useMediaQuery,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { axiosRequest } from '../../../service/AxiosService';
import theme from "../../../style/theme";
import { Reporter } from '../ApplicationData';

interface ReportFile {
  id: string;
  fileName: string;
}

interface ResponseActivity {
  activityId: string;
  reportId: string;
  value: string;
  order: number;
  date: Date;
  scheduledReporterList: Reporter[];
  reporter: Reporter;
  reportFiles?: ReportFile[];
}

interface Props {
  responseActivityList?: ResponseActivity[];
}

/**
 * 応答アクティビティ一覧コンポーネント
 * - 各応答アクティビティの報告者・報告内容・添付ファイルを一覧表示する
 * - 報告者またはdateが存在しない応答は表示しない
 * - props: responseActivityList (応答アクティビティのリスト)
 */
export const ResponseActivityListView: React.FC<Props> = ({ responseActivityList }) => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px以下の場合はスマートフォンデバイス
  // 添付ファイルをダウンロードする
  const handleDownloadFile = async (fileId: string, fileName: string) => {
    try {
      const blob = await axiosRequest<Blob>(
        "/download/file",
        "POST",
        {
          params: { fileId },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("ファイルダウンロードエラー:", error);
    } finally {
    }
  }

  if (!responseActivityList || responseActivityList.length === 0 ||
    responseActivityList.every(activity => !activity.reporter)) {
    return null;
  }

  // 有効な応答のみをフィルタリング（reporterとdateの両方が存在する）
  const validResponseActivities = responseActivityList.filter(
    activity => activity.reporter && activity.date
  );

  if (validResponseActivities.length === 0) {
    return null;
  }
  console.log(responseActivityList);
  //TODO ReportAndUpload.tsx側とまったく同じBoxの作りなのでBoxレイアウトの調整は本来ここでは行わない。
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        mx: isMobile ? 1 : 4,
        my: 2,
        p: "10px 40px",
        position: "center",
        border: "1px solid #ccc",
        borderRadius: "2px",
        backgroundColor: theme.palette.secondary.main,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          mt: 2,
        }}
      >
        <List>
          {validResponseActivities.map((activity) => (
            <React.Fragment key={activity.reportId}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">
                        {activity.reporter?.lastName ?? ''} {activity.reporter?.firstName ?? ''}（{new Date(activity.date).toLocaleString()}）
                      </Typography>
                      <Typography variant="h6"sx={{ whiteSpace: "pre-line" }}>{activity.value}</Typography>
                    </Box>
                  }
                  secondary={
                    <Box mt={1}>
                      {activity.reportFiles && activity.reportFiles.length > 0 && (
                        <Stack spacing={1}>
                          {activity.reportFiles.map((file) => (
                            <Box key={file.id} display="flex" alignItems="center">
                              <Typography variant="body2" sx={{ mr: 1 }}>
                                {file.fileName}
                              </Typography>
                              <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={() => handleDownloadFile(file.id, file.fileName)}
                              >
                                ダウンロード
                              </Button>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              <Divider component="li" />
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );
};
